import React from 'react'
import { Colors } from '@kvika/audur-theme'
import { ProductSchema } from '@kvika/audur-api-types'

import { GridItem, MainGridContainer } from '../../grid-container'
import Calculator from '../../../container/calculator/Calculator'
import { AudurMarketInterestRatesConnectionConnection, Maybe } from '../../../prismic/prismicTypes'
import { CalculatorTab, Gutters } from '../../../utils/types/Types'

type CalculatorSliceData = {
	type: 'calculator'
	primary?: {
		title?: Maybe<string>
		description?: Maybe<string>
		default_tab?: Maybe<string>
		background_color?: Maybe<string>
	}
}

type Props = {
	slice: CalculatorSliceData
	allAudurProducts: ProductSchema[]
	allAudurMarketInterestRates: AudurMarketInterestRatesConnectionConnection
}

const CalculatorSlice: React.FC<Props> = ({ slice, allAudurProducts, allAudurMarketInterestRates }) => {
	const { primary } = slice

	// Handle case where primary data isn't available yet (schema not deployed)
	if (!primary) {
		return (
			<MainGridContainer
				backgroundColor={Colors.Silver}
				gutters={Gutters.None}
				style={{ paddingTop: 64, paddingBottom: 64 }}
			>
				<GridItem className='grid-columns-all'>
					<Calculator
						allAudurProducts={allAudurProducts}
						allAudurMarketInterestRates={allAudurMarketInterestRates}
						defaultTab={CalculatorTab.CompanyCalculator}
					/>
				</GridItem>
			</MainGridContainer>
		)
	}

	// Map string to CalculatorTab enum
	const getCalculatorTab = (tabString: string): CalculatorTab => {
		switch (tabString) {
			case 'SavingsCalculator':
				return CalculatorTab.SavingsCalculator
			case 'TermCalculator':
				return CalculatorTab.TermCalculator
			case 'FutureCalculator':
				return CalculatorTab.FutureCalculator
			case 'CompanyCalculator':
				return CalculatorTab.CompanyCalculator
			default:
				return CalculatorTab.SavingsCalculator
		}
	}

	// Map background color string to Colors enum
	const getBackgroundColor = (colorString: string): Colors => {
		switch (colorString) {
			case 'LIGHT_ORANGE':
				return Colors.LightOrange
			case 'SILVER':
				return Colors.Silver
			case 'TRANSPARENT':
				return Colors.Transparent
			default:
				return Colors.LightOrange
		}
	}

	const defaultTab = primary.default_tab ? getCalculatorTab(primary.default_tab) : CalculatorTab.CompanyCalculator
	const backgroundColor = primary.background_color ? getBackgroundColor(primary.background_color) : Colors.Silver

	return (
		<MainGridContainer
			backgroundColor={backgroundColor}
			gutters={Gutters.None}
			style={{ paddingTop: 64, paddingBottom: 64 }}
		>
			<GridItem className='grid-columns-all'>
				{primary.title && (
					<div style={{ textAlign: 'center', marginBottom: 32 }}>
						<h2>{primary.title}</h2>
					</div>
				)}
				{primary.description && (
					<div style={{ textAlign: 'center', marginBottom: 32 }}>
						<div>{primary.description}</div>
					</div>
				)}
				<Calculator
					allAudurProducts={allAudurProducts}
					allAudurMarketInterestRates={allAudurMarketInterestRates}
					defaultTab={defaultTab}
				/>
			</GridItem>
		</MainGridContainer>
	)
}

export default CalculatorSlice
